import { useEffect, useState } from 'react'
import { api, type BefundBild } from '../api'
import { anzahl, datumKurz, datumLang, euro } from '../format'
import { Karte } from '../komponenten/Karte'
import { Kennzahl } from '../komponenten/Kennzahl'

const SICHERHEIT: Record<string, string> = {
  gesichert: 'steht so in den Daten',
  abgeleitet: 'aus den Daten gefolgert',
  vermutet: 'vermutet — zählt nicht',
}

export function Pruefmodul({ von, bis, betriebe }: {
  von: string; bis: string; betriebe: string[]
}) {
  const [d, setD] = useState<BefundBild | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [laedt, setLaedt] = useState(true)
  const [katalogOffen, setKatalogOffen] = useState(false)

  useEffect(() => {
    let weg = false
    setLaedt(true); setFehler(null)
    api.befunde(von, bis, betriebe)
      .then((x) => { if (!weg) setD(x) })
      .catch((e) => { if (!weg) setFehler(e.message) })
      .finally(() => { if (!weg) setLaedt(false) })
    return () => { weg = true }
  }, [von, bis, betriebe.join(',')])

  if (fehler) return <div className="fehler">{fehler}</div>
  if (laedt && !d) return <div className="laedt">Wird geladen …</div>
  if (!d) return null

  if (!d.geprueft) {
    return (
      <div className="karte"><div className="leer" style={{ maxWidth: '62ch', margin: '0 auto', lineHeight: 1.7 }}>
        <b style={{ color: 'var(--schrift)' }}>Für diesen Zeitraum ist das Regelwerk nicht gelaufen.</b>
        <div style={{ marginTop: 10 }}>
          Das ist etwas anderes als „nichts gefunden". Geprüft wird beim
          Import; für Tage, die vorher geladen wurden, muss der Lauf
          nachgeholt werden.
        </div>
      </div></div>
    )
  }

  const k = d.kopf
  const nichtGebaut = d.katalog.filter((r) => !r.gebaut)
  const mehrereStandorte = new Set(d.liste.map((b) => b.betrieb_id)).size > 1

  return (
    <>
      <div className="gitter g-4" style={{ marginBottom: 14 }}>
        <Kennzahl etikett="Befunde" wert={anzahl(k.befunde)}
                  fuss={k.verdraengt
                    ? `${anzahl(k.verdraengt)} davon verdrängt`
                    : `aus ${anzahl(k.regeln)} Regeln`} />
        <Kennzahl etikett="Punkte" wert={anzahl(k.punkte)}
                  fuss="gewichtete Schwere, nicht Geld" />
        <Kennzahl etikett="Betroffene Belege" wert={anzahl(k.belege)}
                  fuss={k.betrag ? `${euro(k.betrag)} berührt` : undefined} />
        <Kennzahl etikett="Personen" wert={anzahl(k.personen)}
                  fuss={`${anzahl(d.lauf.regeln)} Regeln liefen`} />
      </div>

      <div className="gitter g-2" style={{ marginBottom: 14 }}>
        <Karte titel="Wonach gesucht wurde" neben={`Regelwerk ${d.lauf.fassung}`}>
          <table>
            <thead>
              <tr>
                <th>Regel</th>
                <th className="r">Fälle</th>
                <th className="r">je Fall</th>
                <th className="r">Punkte</th>
              </tr>
            </thead>
            <tbody>
              {d.regeln.map((r) => (
                <tr key={r.regel}>
                  <td>
                    <span className="zahl">{r.regel}</span>
                    <span style={{ marginLeft: 8 }}>{r.name}</span>
                  </td>
                  <td className="r zahl">{anzahl(r.anzahl)}</td>
                  <td className="r zahl dezent">{anzahl(r.punkte_je_fall)}</td>
                  <td className="r zahl">{anzahl(r.punkte)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="dezent" style={{ fontSize: 11, marginTop: 12, marginBottom: 0, lineHeight: 1.6 }}>
            {anzahl(nichtGebaut.length)} von {anzahl(d.katalog.length)} Regeln
            des Katalogs rechnen noch nicht.{' '}
            <button type="button" className="verweis"
                    onClick={() => setKatalogOffen((x) => !x)}>
              {katalogOffen ? 'Liste schließen' : 'Zeigen, wonach nicht gesucht wurde'}
            </button>
          </p>
          {katalogOffen && (
            <div style={{ maxHeight: 260, overflowY: 'auto', marginTop: 10 }}>
              <table>
                <tbody>
                  {nichtGebaut.map((r) => (
                    <tr key={r.code}>
                      <td style={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                        <span className="zahl">{r.code}</span>
                      </td>
                      <td style={{ verticalAlign: 'top' }}>{r.name}</td>
                      <td className="dezent" style={{ fontSize: 11, lineHeight: 1.5 }}>
                        {r.fehlende_quelle}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Karte>

        <Karte titel="Punkte je Kennung"
               neben="wo hinzusehen ist — kein Urteil">
          {d.personen.length ? (
            <>
              <table>
                <thead>
                  <tr>
                    <th>Kennung</th>
                    <th className="r">Befunde</th>
                    <th className="r">aus Regeln</th>
                    <th className="r">schwerster</th>
                    <th className="r">Punkte</th>
                  </tr>
                </thead>
                <tbody>
                  {d.personen.map((p) => (
                    <tr key={p.person_id}>
                      <td>
                        <span className="zahl">{p.kennung}</span>
                        {p.ueber_abschliesser > 0 && (
                          <span className="dezent" style={{ marginLeft: 7, fontSize: 11 }}>
                            {p.ueber_abschliesser}× über den Abschließer
                          </span>
                        )}
                      </td>
                      <td className="r zahl">{anzahl(p.befunde)}</td>
                      <td className="r zahl dezent">{anzahl(p.regeln)}</td>
                      <td className="r zahl dezent">{anzahl(p.schwerster)}</td>
                      <td className="r zahl">{anzahl(p.punkte)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="dezent" style={{ fontSize: 11, marginTop: 12, marginBottom: 0, lineHeight: 1.6, maxWidth: '70ch' }}>
                Zwanzig Punkte aus einer Regel sind etwas anderes als
                zwanzig aus vier — deshalb steht die Zahl der Regeln
                daneben. Und wer viel abkassiert, taucht hier eher auf:
                Schichtleitungen korrigieren für andere mit.
              </p>
            </>
          ) : (
            <div className="leer" style={{ lineHeight: 1.7 }}>
              Für diesen Mandanten wird die Bedienerdimension nicht
              geführt. Befunde entstehen, aber keiner Person zugeordnet.
            </div>
          )}
        </Karte>
      </div>

      <Karte titel="Befunde" neben="schwerster zuerst">
        {d.liste.length ? (
          <div style={{ display: 'grid', gap: 10 }}>
            {d.liste.map((b) => (
              <article key={b.id} className="befund">
                <div className="befund-kopf">
                  <span className="befund-punkte zahl">{b.punkte_gewertet}</span>
                  <span className="zahl dezent">{b.regel}</span>
                  <b>{b.regel_name}</b>
                  <span className="schieber" />
                  <span className="dezent zahl">
                    {datumKurz(b.tag)}{b.uhrzeit ? ` ${b.uhrzeit}` : ''}
                  </span>
                  {b.kennung && <span className="zahl dezent">{b.kennung}</span>}
                  {mehrereStandorte && <span className="dezent">{b.betrieb}</span>}
                </div>
                <p className="befund-text">{b.erklaerung}</p>
                <div className="befund-fuss dezent">
                  {b.sicherheit !== 'gesichert' && (
                    <span className="merker">{SICHERHEIT[b.sicherheit]}</span>
                  )}
                  {b.status === 'VERDRAENGT' && (
                    <span className="merker">
                      verdrängt von {b.verdraengt_von} — zählt nicht
                    </span>
                  )}
                  {b.zurechnung === 'CLOSER'
                    && b.regel_zurechnung !== 'CLOSER' && (
                    <span className="merker">
                      über den Abschließer zugerechnet
                    </span>
                  )}
                  {b.beleg && <span className="zahl">{b.beleg}</span>}
                  {b.tisch && <span>Tisch {b.tisch}</span>}
                  {b.betrag && <span className="zahl">{euro(b.betrag)}</span>}
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="leer" style={{ maxWidth: '62ch', lineHeight: 1.7 }}>
            Keine Befunde in diesem Zeitraum — bei {anzahl(d.lauf.regeln)}{' '}
            Regeln, die gelaufen sind. Was die übrigen betrifft, steht
            oben unter „wonach nicht gesucht wurde".
          </div>
        )}
        <p className="dezent" style={{ fontSize: 11, marginTop: 14, marginBottom: 0, lineHeight: 1.6, maxWidth: '78ch' }}>
          Ein Befund sagt nicht, dass jemand etwas falsch gemacht hat. Er
          sagt: hier ist etwas geschehen, das nachzusehen lohnt — und
          zwar dieses hier, um diese Zeit, in dieser Höhe. Zuletzt
          geprüft am {datumLang(d.lauf.zuletzt?.slice(0, 10))}.
        </p>
      </Karte>
    </>
  )
}
